/**
 * Simulation Engine
 * Provides deterministic simulation of game state changes for testing
 */

import { gameLogger } from '../../utils/GameLogger';

export interface SimulationState {
  frame: number;
  timestamp: number;
  deltaTime: number;
  entities: Map<string, SimulatedEntity>;
  events: SimulationEvent[];
}

export interface SimulatedEntity {
  id: string;
  type: 'player' | 'npc' | 'projectile' | 'item';
  x: number;
  y: number;
  vx: number;
  vy: number;
  health: number;
  maxHealth: number;
  level?: number;
  xp?: number;
  data: Record<string, any>;
}

export interface SimulationEvent {
  frame: number;
  type: string;
  sourceId: string;
  targetId?: string;
  data: Record<string, any>;
}

export interface SimulationConfig {
  targetFPS: number;
  maxFrames: number;
  deterministic: boolean;
  recordEvents: boolean;
  gravity: number;
  friction: number;
}

export const DEFAULT_SIMULATION_CONFIG: SimulationConfig = {
  targetFPS: 60,
  maxFrames: 3600, // 1 minute at 60 FPS
  deterministic: true,
  recordEvents: true,
  gravity: 980,
  friction: 0.8
};

/**
 * Simulation Engine for deterministic game state testing
 */
export class SimulationEngine {
  private state: SimulationState;
  private config: SimulationConfig;
  private running: boolean = false;
  private frameInterval: number | null = null;
  private seed: number;

  constructor(config: Partial<SimulationConfig> = {}, seed: number = 12345) {
    this.config = { ...DEFAULT_SIMULATION_CONFIG, ...config };
    this.seed = seed;
    this.state = this.createInitialState();
  }

  /**
   * Create initial simulation state
   */
  private createInitialState(): SimulationState {
    return {
      frame: 0,
      timestamp: 0,
      deltaTime: 1000 / this.config.targetFPS,
      entities: new Map(),
      events: []
    };
  }

  /**
   * Reset simulation to initial state
   */
  reset(): void {
    this.stop();
    this.state = this.createInitialState();
    this.seed = 12345;
    gameLogger.info('test', 'Simulation engine reset');
  }

  /**
   * Start simulation loop
   */
  start(): void {
    if (this.running) return;
    
    this.running = true;
    // Event: start
    
    const frameTime = 1000 / this.config.targetFPS;
    
    this.frameInterval = window.setInterval(() => {
      this.step();
    }, frameTime);

    gameLogger.info('test', 'Simulation engine started', { fps: this.config.targetFPS });
  }

  /**
   * Stop simulation loop
   */
  stop(): void {
    if (!this.running) return;
    
    this.running = false;
    
    if (this.frameInterval !== null) {
      clearInterval(this.frameInterval);
      this.frameInterval = null;
    }

    // Event: stop
    gameLogger.info('test', 'Simulation engine stopped', { frames: this.state.frame });
  }

  /**
   * Execute single simulation step
   */
  step(): void {
    if (this.state.frame >= this.config.maxFrames) {
      this.stop();
      return;
    }

    this.state.frame++;
    this.state.timestamp += this.state.deltaTime;

    // Update all entities
    this.updateEntities();

    // Check collisions
    this.checkCollisions();

    // Event: step
  }

  /**
   * Update all entities
   */
  private updateEntities(): void {
    const dt = this.state.deltaTime / 1000; // Convert to seconds

    for (const entity of this.state.entities.values()) {
      // Apply gravity
      entity.vy += this.config.gravity * dt;

      // Apply friction
      entity.vx *= this.config.friction;
      entity.vy *= this.config.friction;

      // Update position
      entity.x += entity.vx * dt;
      entity.y += entity.vy * dt;

      // Floor collision
      if (entity.y > 600) {
        entity.y = 600;
        entity.vy = 0;
      }

      // Bounds
      if (entity.x < 0) {
        entity.x = 0;
        entity.vx = 0;
      }
      if (entity.x > 1600) {
        entity.x = 1600;
        entity.vx = 0;
      }
    }
  }

  /**
   * Check collisions between entities
   */
  private checkCollisions(): void {
    const entities = Array.from(this.state.entities.values());

    for (let i = 0; i < entities.length; i++) {
      for (let j = i + 1; j < entities.length; j++) {
        const entityA = entities[i];
        const entityB = entities[j];

        if (this.entitiesCollide(entityA, entityB)) {
          this.handleCollision(entityA, entityB);
        }
      }
    }
  }

  /**
   * Check if two entities collide
   */
  private entitiesCollide(a: SimulatedEntity, b: SimulatedEntity): boolean {
    const sizeA = a.type === 'player' ? 32 : 16;
    const sizeB = b.type === 'player' ? 32 : 16;

    const dx = Math.abs(a.x - b.x);
    const dy = Math.abs(a.y - b.y);

    return dx < (sizeA + sizeB) / 2 && dy < (sizeA + sizeB) / 2;
  }

  /**
   * Handle collision between two entities
   */
  private handleCollision(a: SimulatedEntity, b: SimulatedEntity): void {
    // Bounce
    const tempVx = a.vx;
    a.vx = b.vx * 0.5;
    b.vx = tempVx * 0.5;

    const tempVy = a.vy;
    a.vy = b.vy * 0.5;
    b.vy = tempVy * 0.5;

    // Separate entities
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const overlap = 32 - dist;

    a.x -= (dx / dist) * overlap * 0.5;
    a.y -= (dy / dist) * overlap * 0.5;
    b.x += (dx / dist) * overlap * 0.5;
    b.y += (dy / dist) * overlap * 0.5;

    // Record event
    this.recordEvent('collision', a.id, b.id, { damage: 0 });
  }

  /**
   * Record a simulation event
   */
  private recordEvent(
    type: string,
    sourceId: string,
    targetId?: string,
    data: Record<string, any> = {}
  ): void {
    if (!this.config.recordEvents) return;

    const event: SimulationEvent = {
      frame: this.state.frame,
      type,
      sourceId,
      targetId,
      data
    };

    this.state.events.push(event);
  }

  /**
   * Add entity to simulation
   */
  addEntity(entity: Partial<SimulatedEntity>): SimulatedEntity {
    const fullEntity: SimulatedEntity = {
      id: entity.id || `entity_${this.state.entities.size}`,
      type: entity.type || 'npc',
      x: entity.x ?? 400,
      y: entity.y ?? 300,
      vx: entity.vx ?? 0,
      vy: entity.vy ?? 0,
      health: entity.health ?? 100,
      maxHealth: entity.maxHealth ?? 100,
      level: entity.level ?? 1,
      xp: entity.xp ?? 0,
      data: entity.data ?? {}
    };

    this.state.entities.set(fullEntity.id, fullEntity);
    // Event: entityAdded

    return fullEntity;
  }

  /**
   * Remove entity from simulation
   */
  removeEntity(id: string): boolean {
    const entity = this.state.entities.get(id);
    if (!entity) return false;

    this.state.entities.delete(id);
    // Event: entityRemoved
    return true;
  }

  /**
   * Get entity by ID
   */
  getEntity(id: string): SimulatedEntity | undefined {
    return this.state.entities.get(id);
  }

  /**
   * Apply damage to entity
   */
  damageEntity(id: string, damage: number, sourceId: string): boolean {
    const entity = this.state.entities.get(id);
    if (!entity) return false;

    entity.health = Math.max(0, entity.health - damage);
    
    this.recordEvent('damage', sourceId, id, { damage, remainingHealth: entity.health });

    if (entity.health <= 0) {
      this.recordEvent('death', sourceId, id, { xpReward: entity.xp || 0 });
      // Event: entityDeath
    }

    return true;
  }

  /**
   * Apply force to entity
   */
  applyForce(id: string, fx: number, fy: number): boolean {
    const entity = this.state.entities.get(id);
    if (!entity) return false;

    entity.vx += fx;
    entity.vy += fy;

    return true;
  }

  /**
   * Teleport entity to position
   */
  teleportEntity(id: string, x: number, y: number): boolean {
    const entity = this.state.entities.get(id);
    if (!entity) return false;

    entity.x = x;
    entity.y = y;
    entity.vx = 0;
    entity.vy = 0;

    this.recordEvent('teleport', id, undefined, { x, y });

    return true;
  }

  /**
   * Award XP to entity
   */
  awardXP(id: string, amount: number): boolean {
    const entity = this.state.entities.get(id);
    if (!entity || entity.type !== 'player') return false;

    entity.xp = (entity.xp || 0) + amount;
    
    // Simple level up logic
    const xpNeeded = (entity.level || 1) * 100;
    if (entity.xp >= xpNeeded) {
      entity.level = (entity.level || 1) + 1;
      entity.xp -= xpNeeded;
      entity.maxHealth += 10;
      entity.health = entity.maxHealth;
      
      this.recordEvent('levelUp', id, undefined, { newLevel: entity.level });
    }

    this.recordEvent('xpAward', 'system', id, { amount, totalXP: entity.xp });

    return true;
  }

  /**
   * Get current simulation state
   */
  getState(): SimulationState {
    return this.state;
  }

  /**
   * Get simulation statistics
   */
  getStats(): Record<string, number> {
    return {
      frames: this.state.frame,
      entities: this.state.entities.size,
      events: this.state.events.length,
      runtime: this.state.timestamp
    };
  }

  /**
   * Run simulation for a fixed number of frames
   */
  async runFrames(frames: number): Promise<void> {
    for (let i = 0; i < frames; i++) {
      this.step();
      
      // Yield to event loop every 100 frames
      if (i % 100 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  /**
   * Run simulation for a fixed duration
   */
  async runDuration(milliseconds: number): Promise<void> {
    const targetFrame = this.state.frame + Math.ceil(milliseconds / this.state.deltaTime);
    await this.runFrames(targetFrame - this.state.frame);
  }

  /**
   * Export simulation replay
   */
  exportReplay(): string {
    const replay = {
      config: this.config,
      seed: this.seed,
      finalState: {
        frame: this.state.frame,
        timestamp: this.state.timestamp,
        entities: Array.from(this.state.entities.entries()),
        events: this.state.events
      }
    };

    return JSON.stringify(replay, null, 2);
  }

  /**
   * Load simulation replay
   */
  loadReplay(replayJson: string): void {
    const replay = JSON.parse(replayJson);
    
    this.config = { ...DEFAULT_SIMULATION_CONFIG, ...replay.config };
    this.seed = replay.seed;
    
    this.state = {
      frame: replay.finalState.frame,
      timestamp: replay.finalState.timestamp,
      deltaTime: 1000 / this.config.targetFPS,
      entities: new Map(replay.finalState.entities),
      events: replay.finalState.events
    };
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stop();
    this.state.entities.clear();
    this.state.events = [];
  }
}

// Singleton instance
let simulationEngine: SimulationEngine | null = null;

export function getSimulationEngine(config?: Partial<SimulationConfig>): SimulationEngine {
  if (!simulationEngine) {
    simulationEngine = new SimulationEngine(config);
  }
  return simulationEngine;
}

export function resetSimulationEngine(): void {
  if (simulationEngine) {
    simulationEngine.dispose();
    simulationEngine = null;
  }
}

export default SimulationEngine;
